import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';

interface MembersResponse {
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    profile_image_url?: string;
    role: 'leader' | 'member';
  }>;
}

export interface MemberPhotoMap {
  /** PG user id → fresh presigned photo URL. */
  byId: Map<string, string>;
  /** Display name → fresh presigned photo URL (message docs only carry
   *  Firebase UIDs + userName, so name is the join key for sender avatars). */
  byName: Map<string, string>;
}

/**
 * Fresh member photo URLs for the current org's groups.
 *
 * Photo URLs stored in Firestore (dmParticipants, message docs) are presigned
 * S3 URLs captured at write time — they expire, which is why chat avatars
 * "break". GET /groups/:id/members re-presigns on every fetch, so this map is
 * the live source of truth (mobile does the same via memberPhotoMap).
 *
 * Scoped to the current org: member fetches are org-context bound (RLS), so
 * cross-org rows keep their fallback until the user enters that org.
 */
export function useMemberPhotoMap(): MemberPhotoMap {
  const { currentOrganization, organizations, isSwitchingOrg } = useAuth();
  const orgId = currentOrganization?.id;

  const groupIds = useMemo(() => {
    const membership = organizations.find((o) => o.organization_id === orgId);
    return (membership?.groups ?? []).map((g) => g.id);
  }, [organizations, orgId]);

  const results = useQueries({
    queries: groupIds.map((groupId) => ({
      // Same key shape as useGroupMembers so the caches are shared
      queryKey: [orgId, groupId, 'group-members'],
      enabled: Boolean(orgId && !isSwitchingOrg),
      staleTime: 5 * 60_000,
      queryFn: async () => {
        const response = await api.get<MembersResponse>(`/groups/${groupId}/members`);
        return (response.data || []).map((m) => ({
          userId: m.id,
          userName: m.name,
          profileImageUrl: m.profile_image_url,
          role: m.role,
        }));
      },
    })),
  });

  return useMemo(() => {
    const byId = new Map<string, string>();
    const byName = new Map<string, string>();
    for (const result of results) {
      for (const member of result.data ?? []) {
        if (member.profileImageUrl) {
          byId.set(member.userId, member.profileImageUrl);
          byName.set(member.userName, member.profileImageUrl);
        }
      }
    }
    return { byId, byName };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.dataUpdatedAt).join(',')]);
}

export default useMemberPhotoMap;
